// Разбор ссылок (aviasales/kiwi/skyscanner/google-flights-подобных) в черновики рейсов.
// Чистый модуль: только разбор строк, никаких сетевых запросов (fetch/XHR/img).

export interface UrlLeg {
  from: string;
  to: string;
  /** 'YYYY-MM-DD' или '' — время в URL никогда не приходит */
  departDate: string;
  notes: string[];
}

export interface UrlParseResult {
  legs: UrlLeg[];
  notes: string[];
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function isoDate(y: number, m: number, d: number): string {
  return `${y.toString().padStart(4, '0')}-${pad2(m)}-${pad2(d)}`;
}

/** Извлекает http(s)-ссылки из произвольного текста. */
export function findUrls(text: string): string[] {
  const re = /https?:\/\/[^\s"'<>)]+/gi;
  return text.match(re) ?? [];
}

/**
 * Год для aviasales-подобного ддмм: если полученная дата в контексте
 * opts.year (или текущего года, когда opts.year не задан) уже в прошлом —
 * берём следующий год. При явном opts.year откат назад не делаем.
 */
function resolveAviasalesYear(day: number, month: number, opts?: { year?: number }): number {
  const now = new Date();
  const refYear = opts?.year ?? now.getFullYear();
  const candidateUTC = Date.UTC(refYear, month - 1, day);
  const todayUTC =
    opts?.year != null
      ? Date.UTC(refYear, 0, 1)
      : Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return candidateUTC < todayUTC ? refYear + 1 : refYear;
}

function resolveAviasalesDate(day: number, month: number, opts?: { year?: number }): string {
  const year = resolveAviasalesYear(day, month, opts);
  return isoDate(year, month, day);
}

interface ChainToken {
  code: string;
  ddmm: string;
}

function tokenizeChain(chain: string): ChainToken[] {
  const tokens: ChainToken[] = [];
  let i = 0;
  while (i < chain.length) {
    const code = chain.slice(i, i + 3);
    if (!/^[A-Z]{3}$/.test(code)) break;
    i += 3;
    const maybeDate = chain.slice(i, i + 4);
    let ddmm = '';
    if (/^\d{4}$/.test(maybeDate)) {
      ddmm = maybeDate;
      i += 4;
    }
    tokens.push({ code, ddmm });
  }
  return tokens;
}

function makeLeg(fromCode: string, toCode: string, ddmm: string, opts?: { year?: number }): UrlLeg {
  let departDate = '';
  if (ddmm) {
    const day = parseInt(ddmm.slice(0, 2), 10);
    const month = parseInt(ddmm.slice(2, 4), 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      departDate = resolveAviasalesDate(day, month, opts);
    }
  }
  return { from: fromCode, to: toCode, departDate, notes: [] };
}

/** Разбирает одну ссылку в один или несколько черновиков (без времени). */
export function parseUrl(urlStr: string, opts?: { year?: number }): UrlParseResult {
  const notes: string[] = [];

  // 1) aviasales-подобная цепочка кодов: LCA2712EVN1601, LCA2712EVN, LCA2712IST0301EVN...
  const chainRe = /[A-Z]{3}(?:\d{4}[A-Z]{3})+(?:\d{4})?/g;
  const upper = urlStr.toUpperCase();
  const chainMatches: string[] = [];
  let cm: RegExpExecArray | null;
  while ((cm = chainRe.exec(upper))) {
    chainMatches.push(cm[0]);
  }

  const legs: UrlLeg[] = [];
  for (const chain of chainMatches) {
    const tokens = tokenizeChain(chain);
    if (tokens.length < 2) continue;
    if (tokens.length === 2) {
      legs.push(makeLeg(tokens[0].code, tokens[1].code, tokens[0].ddmm, opts));
      if (tokens[1].ddmm) {
        legs.push(makeLeg(tokens[1].code, tokens[0].code, tokens[1].ddmm, opts));
      }
    } else {
      for (let i = 0; i < tokens.length - 1; i++) {
        legs.push(makeLeg(tokens[i].code, tokens[i + 1].code, tokens[i].ddmm, opts));
      }
    }
    notes.push(`Из ссылки распознан аэрокод-маршрут «${chain}».`);
  }
  if (legs.length > 0) {
    return { legs, notes };
  }

  // 2) kiwi/skyscanner/google-flights-подобный путь: /LCA/EVN/2026-12-27
  const pathRe = /\/([A-Za-z]{3})\/([A-Za-z]{3})\/(\d{4}-\d{2}-\d{2})/;
  const pathMatch = pathRe.exec(urlStr);
  if (pathMatch) {
    legs.push({
      from: pathMatch[1].toUpperCase(),
      to: pathMatch[2].toUpperCase(),
      departDate: pathMatch[3],
      notes: [],
    });
    notes.push('Маршрут и дата распознаны из пути ссылки.');
    return { legs, notes };
  }

  // 3) query-параметры: origin/destination/depart_date, from/to/date
  let params: URLSearchParams | null = null;
  try {
    params = new URL(urlStr).searchParams;
  } catch {
    const qIndex = urlStr.indexOf('?');
    if (qIndex >= 0) {
      try {
        params = new URLSearchParams(urlStr.slice(qIndex + 1));
      } catch {
        params = null;
      }
    }
  }
  if (params) {
    const fromRaw = (params.get('origin') || params.get('from') || '').toUpperCase();
    const toRaw = (params.get('destination') || params.get('to') || '').toUpperCase();
    const dateRaw = params.get('depart_date') || params.get('date') || '';
    const from = /^[A-Z]{3}$/.test(fromRaw) ? fromRaw : '';
    const to = /^[A-Z]{3}$/.test(toRaw) ? toRaw : '';
    const departDate = /^\d{4}-\d{2}-\d{2}$/.test(dateRaw) ? dateRaw : '';
    if (from || to || departDate) {
      legs.push({ from, to, departDate, notes: [] });
      notes.push('Маршрут и/или дата распознаны из query-параметров ссылки.');
      return { legs, notes };
    }
  }

  return { legs: [], notes: [] };
}
